/**
 * Request File Search API
 * 
 * Wrapper for searching request files using Elasticsearch
 */
import { v1Api } from '../../../api/generatedClient'
import type { APIRequestFileSearchResponse } from '../../../generated-types/models/APIRequestFileSearchResponse'
import type { APIRequestFileSearchResult } from '../../../generated-types/models/APIRequestFileSearchResult'
import type { V1SearchRequestsRequestFilesRetrieveRequest } from '../../../generated-types/apis/V1Api'
import { createFileInfo } from '../../../utils/functions'
import type { FileInfo } from '../../../utils/types'

export interface RequestFileSearchFilters {
  q: string
  requestId?: number
  requestCertificate?: string
  fileName?: string
  storedAt?: string // YYYY-MM-DD
  fileSize?: string // File size (e.g., '10287', '10.05', '10.05 KB', '1.5 MB')
  fields?: string[]
  size?: number
  from?: number
}

export interface RequestFileSearchResponse {
  total: number
  max_score: number | null
  results: FileInfo[]
  page: {
    from: number
    size: number
    has_more: boolean
  }
}

function toRequestFileSearchRequest(
  filters: RequestFileSearchFilters
): V1SearchRequestsRequestFilesRetrieveRequest {
  if (!filters.q || filters.q.trim() === '') {
    throw new Error('Search query (q) is required')
  }

  return {
    q: filters.q,
    fields: filters.fields && filters.fields.length > 0 ? filters.fields.join(',') : undefined,
    size: filters.size,
    from: filters.from,
    requestId: filters.requestId,
    requestCertificate: filters.requestCertificate,
    fileName: filters.fileName,
    storedAt: filters.storedAt ? new Date(filters.storedAt) : undefined,
    fileSize: filters.fileSize,
  }
}

/**
 * Search for request files using Elasticsearch
 * 
 * @param filters - Search filters
 * @returns Request file search response with results and pagination info
 */
export async function searchRequestFiles(
  filters: RequestFileSearchFilters
): Promise<RequestFileSearchResponse> {
  const response: APIRequestFileSearchResponse = await v1Api.v1SearchRequestsRequestFilesRetrieve(
    toRequestFileSearchRequest(filters)
  )

  // Map results to FileInfo
  const fileInfos = response.results.map((result) => {
    const fileInfo: Record<string, unknown> = {
      self: `/v1/request/files/${result.id}/`,
      name: result.fileName ?? '',
      key: result.key ?? '',
      size: result.fileSize ?? 0,
      stored_at: result.storedAt?.toISOString(),
      last_modified_at: result.lastModifiedAt?.toISOString(),
    }

    if (result.requestId) {
      fileInfo.request = `/v1/request/requests/${result.requestId}/`
    }

    return createFileInfo(fileInfo)
  })

  return {
    total: response.total,
    max_score: response.maxScore,
    results: fileInfos,
    page: {
      from: response.page.fromValue,
      size: response.page.size,
      has_more: response.page.hasMore,
    },
  }
}
